class FacetFilters extends HTMLElement {
  constructor() {
    super();
    this.debouncedOnSubmit = debounce((event) => {
      // Ignore price inputs for auto-submit
      if (event.target.closest('.facets__price')) return;
      this.onSubmitHandler(event);
    }, 500);

    this.addEventListener('input', this.debouncedOnSubmit.bind(this));
    // Use 'change' for immediate feedback on checkboxes/radios, but still debounce slightly to batch fast clicks
    this.addEventListener('change', (event) => {
      // If it's a price range input, ignore 'change' (fetched on blur) or let 'input' handle it
      if (event.target.closest('.facets__price')) return;
      this.onSubmitHandler(event);
    });

    // Delegate click for active facets
    this.addEventListener('click', (event) => {
      const link = event.target.closest('.active-facets__button');
      if (link) {
        event.preventDefault();
        event.stopPropagation();

        // Specific filter removal via data attributes (robust)
        if (link.dataset.name && link.dataset.value) {
          const currentParams = new URLSearchParams(window.location.search);
          // Delete specifically the value that matches, handling multiple same-key params if needed
          // Note: URLSearchParams.delete(name, value) exists in modern browsers, but .delete(name) removes ALL.
          // To remove specific value for a key that might have multiple values (like list filters):

          const newParams = new URLSearchParams();
          for (const [key, val] of currentParams.entries()) {
            if (key === link.dataset.name && val === link.dataset.value) {
              continue; // Skip the one we are removing
            }
            if (key === 'page') continue; // Reset pagination when filter changes
            newParams.append(key, val);
          }

          FacetFilters.renderPage(newParams.toString(), null);
        } else if (link.href && link.href.indexOf('#') === -1) {
          // Fallback for Clear All or Price Range (using href)
          const url = new URL(link.href);
          const params = url.searchParams.toString();
          FacetFilters.renderPage(params, null);
        }
      }
    });

    this.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFilters.searchParamsInitial;
      if (searchParams === FacetFilters.searchParamsPrev) return;
      FacetFilters.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    // Filter out default price values unless explicitly applied by the price button
    const isPriceSubmit = event.submitter && event.submitter.classList.contains('facets__price-apply');

    if (!isPriceSubmit) {
      const priceInputs = form.querySelectorAll('.facets__price input');
      for (const input of priceInputs) {
        const isMin = input.id.endsWith('GTE');
        const isMax = input.id.endsWith('LTE');

        // Remove param if value matches the default bounds (0 for min, max attribute for max) or is empty
        if (isMin && (input.value === '0' || input.value === '')) {
          searchParams.delete(input.name);
        }
        if (isMax && (input.value === input.max || input.value === '')) {
          searchParams.delete(input.name);
        }
      }
    }

    FacetFilters.renderPage(searchParams.toString(), event);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFilters.searchParamsPrev = searchParams;
    const sections = FacetFilters.getSections();
    const countContainer = document.getElementById('ProductCount');
    const gridContainer = document.getElementById('ProductGridContainer');

    if (gridContainer) gridContainer.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');

    for (const section of sections) {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFilters.filterData.some(filterDataUrl)
        ? FacetFilters.renderSectionFromCache(filterDataUrl, event)
        : FacetFilters.renderSectionFromFetch(url, event);
    }

    if (updateURLHash) FacetFilters.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    if (FacetFilters.abortController) {
      FacetFilters.abortController.abort();
    }
    FacetFilters.abortController = new AbortController();

    fetch(url, { signal: FacetFilters.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        FacetFilters.filterData = [...FacetFilters.filterData, { html: html, url: url }];
        FacetFilters.renderFilters(html, event);
        FacetFilters.renderProductGrid(html);
        FacetFilters.renderProductCount(html);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
        } else {
          console.error('Error fetching facets:', error);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFilters.filterData.find(filterDataUrl).html;
    FacetFilters.renderFilters(html, event);
    FacetFilters.renderProductGrid(html);
    FacetFilters.renderProductCount(html);
  }

  static renderProductGrid(html) {
    const newGrid = html.getElementById('ProductGridContainer');
    const targetGrid = document.getElementById('ProductGridContainer');
    if (newGrid && targetGrid) {
      targetGrid.innerHTML = newGrid.innerHTML;
      targetGrid.classList.remove('loading');
    }
  }

  static renderProductCount(html) {
    const countSelectors = ['ProductCountDesktop', 'ProductCountMobile', 'ProductCountMobileModal'];

    for (const selector of countSelectors) {
      const newCount = html.getElementById(selector);
      const targetCount = document.getElementById(selector);
      if (newCount && targetCount) {
        targetCount.innerHTML = newCount.innerHTML;
        targetCount.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const newWrapper = html.getElementById('FacetsWrapper');
    const targetWrapper = document.getElementById('FacetsWrapper');
    if (newWrapper && targetWrapper) {
      targetWrapper.innerHTML = newWrapper.innerHTML;
    }

    const newMobileWrapper = html.getElementById('MobileFacetsWrapper');
    const targetMobileWrapper = document.getElementById('MobileFacetsWrapper');
    if (newMobileWrapper && targetMobileWrapper) {
      targetMobileWrapper.innerHTML = newMobileWrapper.innerHTML;
    }

    const newDesktopActive = html.getElementById('ActiveFacetsDesktop');
    const targetDesktopActive = document.getElementById('ActiveFacetsDesktop');
    if (newDesktopActive && targetDesktopActive) {
      targetDesktopActive.innerHTML = newDesktopActive.innerHTML;
    }

    const newMobileActive = html.getElementById('ActiveFacetsMobile');
    const targetMobileActive = document.getElementById('ActiveFacetsMobile');
    if (newMobileActive && targetMobileActive) {
      targetMobileActive.innerHTML = newMobileActive.innerHTML;
    }

    const newMobileModalActive = html.getElementById('ActiveFacetsMobileModal');
    const targetMobileModalActive = document.getElementById('ActiveFacetsMobileModal');
    if (newMobileModalActive && targetMobileModalActive) {
      targetMobileModalActive.innerHTML = newMobileModalActive.innerHTML;
    }

    FacetFilters.initAccordion();
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [{ section: document.getElementById('product-grid').dataset.id }];
  }

  static initAccordion() {
    const detailsElements = document.querySelectorAll('.js-filter');
    for (const details of detailsElements) {
      if (details.dataset.accordionBound) {
        continue;
      }
      details.dataset.accordionBound = 'true';

      details.addEventListener('toggle', (event) => {
        if (event.target.open) {
          // Prevent recursion / layout thrashing
          if (FacetFilters.isAnimating) return;
          FacetFilters.isAnimating = true;

          requestAnimationFrame(() => {
            const container = event.target.parentElement;
            if (container) {
              // Iterate ONLY over direct children to avoid heavy querySelectorAll on the whole subtree
              // and ensures we only close true siblings (other filter groups)
              const children = Array.from(container.children);

              for (const sibling of children) {
                // Check if it's a details element with js-filter class and is NOT the target
                if (sibling !== event.target && sibling.matches('.js-filter') && sibling.hasAttribute('open')) {
                  sibling.removeAttribute('open');
                }
              }
            }

            // Release lock after a short delay or next frame
            requestAnimationFrame(() => {
              FacetFilters.isAnimating = false;
            });
          });
        }
      });
    }
  }

  /**
   * Desktop (#FacetsWrapper): close open filter/sort panels on outside click or Escape
   * (matches theme desktop breakpoint 75em, same as .header__desktop-menu). Bound once — listeners are on document.
   */
  static bindDesktopFacetsDismiss() {
    if (FacetFilters.desktopDismissBound) {
      return;
    }
    FacetFilters.desktopDismissBound = true;

    document.addEventListener('click', (event) => {
      if (!window.matchMedia('(width >= 75em)').matches) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      const openDetailsList = document.querySelectorAll('#FacetsWrapper details.facets__disclosure.js-filter[open]');
      if (openDetailsList.length === 0) {
        return;
      }

      for (const details of openDetailsList) {
        if (!details.contains(target)) {
          details.removeAttribute('open');
        }
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (!window.matchMedia('(width >= 75em)').matches) {
        return;
      }

      const openDetailsList = document.querySelectorAll('#FacetsWrapper details.facets__disclosure.js-filter[open]');
      for (const details of openDetailsList) {
        details.removeAttribute('open');
      }
    });
  }
}

FacetFilters.filterData = [];
FacetFilters.searchParamsInitial = window.location.search.slice(1);
FacetFilters.searchParamsPrev = window.location.search.slice(1);
FacetFilters.desktopDismissBound = false;
customElements.define('facet-filters', FacetFilters);
FacetFilters.setListeners();
FacetFilters.bindDesktopFacetsDismiss();
FacetFilters.initAccordion();

/* Mobile Modal Logic */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetails = this.querySelector('details');

    if (this.mainDetails) {
      this.addEventListener('click', this.onFocusOut.bind(this));
      this.mainDetails.addEventListener('toggle', this.onToggle.bind(this));

      // Close on resize if switching to desktop
      const mediaQuery = window.matchMedia('(width >= 75em)');
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', (e) => {
          if (e.matches && this.mainDetails.hasAttribute('open')) {
            this.close();
          }
        });
      } else {
        // Fallback for older browsers
        mediaQuery.addListener((e) => {
          if (e.matches && this.mainDetails.hasAttribute('open')) {
            this.close();
          }
        });
      }
    }
  }

  onFocusOut(event) {
    const mobileFacetsInner = this.querySelector('.mobile-facets__inner');
    const closeButton = this.querySelector('.mobile-facets__close');

    // Close if clicking outside the inner content (i.e., on the overlay)
    if (mobileFacetsInner && !mobileFacetsInner.contains(event.target) && !closeButton.contains(event.target)) {
      this.close();
    }
  }

  onToggle() {
    if (this.mainDetails.hasAttribute('open')) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  close() {
    this.mainDetails.removeAttribute('open');
    document.body.style.overflow = '';
  }
}

customElements.define('menu-drawer', MenuDrawer);
